import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function SilkscreenReportsPage() {
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    customer: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [screenDetails, setScreenDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      if (filters.customer) params.append('customer', filters.customer);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);
      params.append('format', 'json');

      const response = await fetch(`${API_URL}/silkscreens/usage-report?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.customer) params.append('customer', filters.customer);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);
      params.append('format', 'csv');

      const response = await fetch(`${API_URL}/silkscreens/usage-report?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `silkscreen-usage-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchScreenDetails = async (screenCode) => {
    setLoadingDetails(true);
    try {
      // Find screen by code
      const response = await fetch(`${API_URL}/silkscreens`);
      const data = await response.json();
      const screen = data.screens.find(s => s.screenCode === screenCode);
      
      if (screen) {
        // Fetch full details with history
        const detailResponse = await fetch(`${API_URL}/silkscreens/${screen.id}`);
        const details = await detailResponse.json();
        setScreenDetails(details);
        setSelectedScreen(screenCode);
      }
    } catch (err) {
      console.error('Error fetching screen details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeScreenDetails = () => {
    setSelectedScreen(null);
    setScreenDetails(null);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', position: 'relative' }}>
        <h1 style={{ 
          fontSize: '2rem', 
          fontWeight: '800', 
          color: '#00BCD4',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          📊 Silkscreen Usage Reports
        </h1>
        <p style={{ color: '#666', fontSize: '1rem' }}>
          Track silkscreen checkout history, usage patterns, and activity
        </p>
        
        {/* User Manual Button - Top Right */}
        <button
          onClick={() => window.open('/docs/manuals/silkscreen/current/SILKSCREEN_MANUAL_v1.0.html', '_blank')}
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
          }}
        >
          📖 User Manual
        </button>
      </div>

      {/* Filters Panel */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#333',
          marginBottom: '20px'
        }}>
          Report Filters
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}>
          {/* Customer Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.9rem'
            }}>
              Customer
            </label>
            <select
              name="customer"
              value={filters.customer}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.name}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.9rem'
            }}>
              Date From
            </label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          {/* Date To */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.9rem'
            }}>
              Date To
            </label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          {/* Sort By */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.9rem'
            }}>
              Sort By
            </label>
            <select
              name="sortBy"
              value={filters.sortBy}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="date">Date</option>
              <option value="customer">Customer</option>
              <option value="screenCode">Screen Code</option>
              <option value="impressions">Impressions</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#333',
              fontSize: '0.9rem'
            }}>
              Sort Order
            </label>
            <select
              name="sortOrder"
              value={filters.sortOrder}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '0.95rem',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={fetchReport}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: loading ? '#b0bec5' : '#00BCD4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(0, 188, 212, 0.3)'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.background = '#00ACC1')}
            onMouseLeave={(e) => !loading && (e.target.style.background = '#00BCD4')}
          >
            {loading ? 'Generating...' : '📊 Generate Report'}
          </button>

          {reportData.length > 0 && (
            <button
              onClick={exportToCSV}
              style={{
                padding: '12px 24px',
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
              }}
              onMouseEnter={(e) => e.target.style.background = '#43A047'}
              onMouseLeave={(e) => e.target.style.background = '#4CAF50'}
            >
              📥 Export to CSV
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.95rem'
        }}>
          {error}
        </div>
      )}

      {/* Report Results */}
      {reportData.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#333',
              margin: 0
            }}>
              Report Results
            </h2>
            <div style={{
              background: '#e0f7fa',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#00838f'
            }}>
              {reportData.length} record{reportData.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #00BCD4' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Screen Code</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Customer</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Action</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Work Tickets</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: '#00BCD4' }}>Impressions</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((record, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      background: idx % 2 === 0 ? 'white' : '#fafafa'
                    }}
                  >
                    <td style={{ padding: '12px', color: '#666' }}>
                      {new Date(record.date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => fetchScreenDetails(record.screenCode)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#00BCD4',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          padding: 0
                        }}
                        onMouseOver={(e) => e.target.style.color = '#0097A7'}
                        onMouseOut={(e) => e.target.style.color = '#00BCD4'}
                      >
                        {record.screenCode}
                      </button>
                    </td>
                    <td style={{ padding: '12px', color: '#333' }}>
                      {record.customer}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        background: record.action === 'checkout' ? '#e3f2fd' : '#e8f5e9',
                        color: record.action === 'checkout' ? '#1565c0' : '#2e7d32'
                      }}>
                        {record.action === 'checkout' ? '📤 In Production' : '📥 Return from production'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#666' }}>
                      {record.workTickets || '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#333' }}>
                      {record.impressions ? record.impressions.toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '12px', color: '#666' }}>
                      {record.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && reportData.length === 0 && !error && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 24px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📊</div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
            No Report Generated Yet
          </h3>
          <p style={{ color: '#666', fontSize: '0.95rem' }}>
            Select your filters above and click "Generate Report" to view usage data
          </p>
        </div>
      )}

      {/* Screen Details Modal */}
      {selectedScreen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={closeScreenDetails}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {loadingDetails ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⏳</div>
                <p style={{ color: '#666' }}>Loading screen details...</p>
              </div>
            ) : screenDetails ? (
              <div style={{ padding: '32px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: '800', color: '#00BCD4', marginBottom: '8px' }}>
                      {screenDetails.screenCode}
                    </h2>
                    <p style={{ color: '#666', fontSize: '0.95rem' }}>
                      {screenDetails.currentCustomerName} - {screenDetails.currentItemDescription}
                    </p>
                  </div>
                  <button
                    onClick={closeScreenDetails}
                    style={{
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Close
                  </button>
                </div>

                {/* Screen Info */}
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '24px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Status</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.status}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Quality</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.qualityStatus}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Covered</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.covered ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Date Received</div>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {new Date(screenDetails.dateReceived).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Usage History (Audit Trail) */}
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333', marginBottom: '16px' }}>
                    Usage History
                  </h3>
                  {screenDetails.usageRecords && screenDetails.usageRecords.length > 0 ? (
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {screenDetails.usageRecords.map((record, idx) => (
                        <div key={idx} style={{
                          background: '#fafafa',
                          padding: '16px',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          borderLeft: '4px solid #00BCD4'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ fontWeight: '600', color: '#00BCD4' }}>
                              {record.usageType === 'production' ? '📤 Production' : '🏭 Supplier'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {new Date(record.checkoutDate).toLocaleDateString()}
                            </div>
                          </div>
                          {record.orderNumber && (
                            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                              Work Ticket: {record.orderNumber}
                            </div>
                          )}
                          {record.expectedImpressions && (
                            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                              Impressions: {record.expectedImpressions.toLocaleString()}
                            </div>
                          )}
                          {record.checkinDate && (
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                              <div>Returned: {new Date(record.checkinDate).toLocaleDateString()}</div>
                              <div>Quality: {record.qualityCheckPassed ? '✅ Passed' : '❌ Failed'}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No usage history available</p>
                  )}
                </div>

                {/* Imposition History */}
                {screenDetails.history && screenDetails.history.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333', marginBottom: '16px' }}>
                      Imposition History
                    </h3>
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {screenDetails.history.map((hist, idx) => (
                        <div key={idx} style={{
                          background: '#f0f0f0',
                          padding: '12px',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          fontSize: '0.85rem'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {hist.customerName} - {hist.itemDescription}
                          </div>
                          <div style={{ color: '#666' }}>
                            Imposed: {new Date(hist.dateImposed).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <p style={{ color: '#666' }}>Screen details not found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SilkscreenReportsPage;
